import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CanonicalSkillAutoCompleteResultItem, JobseekerSkillItem, JobseekerSkillViewModel, SkillAutoCompleteResult, SkillBaseTypeEnum } from '@hrra/common-models';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/root-store/state';
import {
  addCustomJobseekerSkill,
  addJobseekerSkill,
  loadSkillSuggestions,
  removeJobseekerSkill,
  reorderJobseekerSkills,
  saveJobseekerSkills,
} from 'src/app/root-store/jobseeker-store/jobseeker-profile/actions';
import {
  selectDraftJobseekerSkills,
  selectHasExactSkillMatch,
  selectJobseekerModerationLastChangeDate,
  selectJobseekerSkills,
  selectSkillSuggestions,
} from 'src/app/root-store/jobseeker-store/jobseeker-profile/selectors';

@Component({
    selector: 'app-jobseeker-skills-edit',
    templateUrl: './jobseeker-skills-edit.component.html',
    standalone: false
})
export class JobseekerSkillsEditComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private currentSkills: JobseekerSkillViewModel[] = [];

  draftSkills$: Observable<JobseekerSkillViewModel[]>;
  savedSkills$: Observable<JobseekerSkillItem[]>;
  suggestions$: Observable<SkillAutoCompleteResult>;
  hasExactMatch$: Observable<boolean>;
  moderationLastChangeDate$: Observable<Date>;

  public title: string;
  public skillBaseType: SkillBaseTypeEnum;

  constructor(
    private store: Store<AppState>,
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<JobseekerSkillsEditComponent>
  ) {}

  ngOnInit(): void {
    this.title = this.data.title;
    this.skillBaseType = this.data.skillBaseType;

    this.draftSkills$ = this.store.pipe(select(selectDraftJobseekerSkills));
    this.savedSkills$ = this.store.pipe(select(selectJobseekerSkills(this.skillBaseType)));
    this.suggestions$ = this.store.pipe(select(selectSkillSuggestions));
    this.hasExactMatch$ = this.store.pipe(select(selectHasExactSkillMatch));

    this.moderationLastChangeDate$ = this.store.pipe(select(selectJobseekerModerationLastChangeDate));

    this.draftSkills$
      .pipe(takeUntil(this.destroy$))
      .subscribe((skills) => (this.currentSkills = skills ?? []));
  }

  onQueryChange(query: string) {
    this.store.dispatch(
      loadSkillSuggestions({
        payload: {
          query: query ?? '',
          skillBaseType: this.skillBaseType,
          selectedCanonicalSkillIds: this.currentSkills
            .filter((s) => !s.isCustom)
            .map((s) => s.canonicalSkillId),
          selectedCustomSkills: this.currentSkills
            .filter((s) => s.isCustom)
            .map((s) => s.name),
        },
      })
    );
  }

  onSelectSkill(skill: CanonicalSkillAutoCompleteResultItem) {
    this.store.dispatch(addJobseekerSkill({ payload: { skill: skill } }));
  }

  onAddCustomSkill(name: string) {
    this.store.dispatch(addCustomJobseekerSkill({ payload: { name: name } }));
  }

  onRemoveSkill(index: number) {
    this.store.dispatch(removeJobseekerSkill({ payload: { index: index } }));
  }

  onReorderSkills(event: { previousIndex: number; currentIndex: number }) {
    this.store.dispatch(reorderJobseekerSkills({ payload: event }));
  }

  onSave(moderationLastChangeDate: Date) {
    this.store.dispatch(
      saveJobseekerSkills({
        payload: { skills: this.currentSkills, skillBaseType: this.skillBaseType, moderationLastChangeDate: moderationLastChangeDate },
      })
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
